import CircularProgress from "@material-ui/core/CircularProgress";
import { InputProps } from "@material-ui/core/Input";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import { FetchMoreProps } from "@saleor/types";
import classNames from "classnames";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { filter } from "fuzzaldrin";
import React from "react";

import ArrowDropdownIcon from "../../icons/ArrowDropdown";
import Debounce, { DebounceProps } from "../Debounce";
import SingleAutocompleteSelectFieldContent, {
  SingleAutocompleteActionType,
  SingleAutocompleteChoiceType
} from "./SingleAutocompleteSelectFieldContent";

const useStyles = makeStyles(
  theme => ({
    container: {
      flexGrow: 1,
      position: "relative"
    },
    loadingOverlay: {
      "& .MuiCircularProgress-root": {
        color: theme.palette.primary.main
      },
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      pointerEvents: "none",
      position: "absolute",
      right: theme.spacing(1),
      top: "50%",
      transform: "translateY(-50%)",
      width: 40,
      zIndex: 5
    },
    textField: {
      "& .MuiOutlinedInput-input": {
        padding: "12px 12px 10px 12px"
      }
    }
  }),
  { name: "SingleAutocompleteSelectField" }
);

export interface SingleAutocompleteSelectFieldProps
  extends Partial<FetchMoreProps> {
  disableOnBlur?: boolean;
  add?: SingleAutocompleteActionType;
  idInputTxt?: string;
  className?: string;
  error?: boolean;
  name: string;
  displayValue: string;
  emptyOption?: boolean;
  choices: SingleAutocompleteChoiceType[];
  value: string | string[];
  disabled?: boolean;
  placeholder?: string;
  allowCustomValues?: boolean;
  helperText?: string;
  label?: string;
  InputProps?: InputProps;
  fetchChoices?: (value: string) => void;
  onChange: (event: React.ChangeEvent<any>) => void;
  required?: boolean;
  retainChoices?: boolean;
  customValueLabel?: string;
  clearOption?: boolean;
  checkValueLabel?: boolean;
  isFromCoupons?: boolean;
}

const DebounceAutocomplete: React.ComponentType<DebounceProps<
  string
>> = Debounce;

const SingleAutocompleteSelectFieldComponent: React.FC<SingleAutocompleteSelectFieldProps> = props => {
  const {
    disableOnBlur = false,
    add,
    allowCustomValues,
    idInputTxt,
    className,
    choices,
    disabled,
    displayValue,
    emptyOption,
    error,
    hasMore,
    helperText,
    label,
    loading,
    name,
    placeholder,
    value,
    InputProps,
    fetchChoices,
    onChange,
    onFetchMore,
    required,
    customValueLabel,
    clearOption = false,
    checkValueLabel = false,
    isFromCoupons,
    ...rest
  } = props;
  const classes = useStyles(props);
  const handleChange = (
    item: any,
    stateAndHelpers: ControllerStateAndHelpers
  ) => {
    let newValue: string;
    if (Array.isArray(item)) {
      newValue = item.join(", ");
    } else {
      newValue = item;
      if (name === "subTenantType") {
        newValue = item || "";
      }
    }
    onChange({
      target: {
        name,
        value: newValue
      }
    } as any);
    stateAndHelpers.reset({
      inputValue: item
    });
  };

  return (
    <DebounceAutocomplete debounceFn={fetchChoices}>
      {debounceFn => (
        <Downshift
          defaultInputValue={displayValue}
          itemToString={() => displayValue || ""}
          onInputValueChange={value => debounceFn(value)}
          onSelect={handleChange}
          selectedItem={value}
        >
          {({
            getInputProps,
            getItemProps,
            isOpen,
            inputValue,
            selectedItem,
            toggleMenu,
            closeMenu,
            highlightedIndex,
            reset
          }) => {
            const isCustomValueSelected =
              choices && selectedItem
                ? choices.filter(c => c.value === selectedItem).length === 0
                : false;

            const choiceFromInputValue =
              choices &&
              choices.find(({ value: choiceId }) => choiceId === inputValue);

            const isValueInValues = !!choiceFromInputValue;

            const isValueInLabels =
              choices && !!choices.find(choice => choice.label === inputValue);

            const ensureProperValues = (alwaysCheck: boolean = false) => {
              if ((allowCustomValues || isValueInLabels) && !alwaysCheck) {
                return;
              }

              if (isValueInValues && !isValueInLabels) {
                reset({ inputValue: choiceFromInputValue.label });
                return;
              } else if (
                checkValueLabel &&
                !isValueInValues &&
                !isValueInLabels
              ) {
                reset({ inputValue: "" });
                onChange({
                  target: {
                    name,
                    value: ""
                  }
                } as any);
                return;
              }

              reset({ inputValue: displayValue });
            };

            const displayCustomValue = !!(
              inputValue &&
              inputValue.length > 0 &&
              allowCustomValues &&
              !isValueInLabels
            );

            const handleBlur = () => {
              ensureProperValues(true);
              if (name === "subTenantType") {
                if (choices.every(option => option.label !== inputValue)) {
                  inputValue = "";
                }
                onChange({
                  target: {
                    name,
                    value: inputValue
                  }
                } as any);
              }
              closeMenu();
            };

            const handleClear = () => {
              ensureProperValues(true);
              onChange({
                target: {
                  name,
                  value: ""
                }
              } as any);
            };

            // fix for bug where input value is returned from debounce as id instead of label
            if (value === inputValue && !!inputValue) {
              ensureProperValues();
            }

            const dropDownAdornment = {
              ...InputProps,
              ...getInputProps({
                placeholder
              }),
              endAdornment: (
                <div>
                  <ArrowDropdownIcon />
                </div>
              ),
              error,
              id: undefined,
              onBlur: disableOnBlur ? undefined : handleBlur,
              onClick: toggleMenu
            };

            const clearValueAdornment = {
              ...InputProps,
              ...getInputProps({
                placeholder
              }),
              endAdornment: (
                <div style={{ cursor: "pointer" }} onClick={handleClear}>
                  <CloseIcon />
                </div>
              ),
              error,
              id: "clearSingleSelectBtn",
              onBlur: disableOnBlur ? undefined : handleBlur,
              onClick: toggleMenu
            };

            return (
              <div
                className={classNames(classes.container, className)}
                {...rest}
              >
                <TextField
                  id={
                    idInputTxt
                      ? idInputTxt
                      : `${label.replace(/ /g, "").toLowerCase()}Txt`
                  }
                  InputProps={
                    clearOption && selectedItem
                      ? clearValueAdornment
                      : dropDownAdornment
                  }
                  className={classNames(null, {
                    [classes.textField]: !label
                  })}
                  error={error}
                  disabled={disabled}
                  helperText={helperText}
                  label={label}
                  required={required}
                  fullWidth={true}
                />
                {loading && isFromCoupons && (
                  <div className={classes.loadingOverlay} aria-hidden>
                    <CircularProgress size={20} />
                  </div>
                )}
                {isOpen && !disabled && (!!inputValue || !!choices.length) && (
                  <SingleAutocompleteSelectFieldContent
                    add={
                      !!add && {
                        ...add,
                        onClick: () => {
                          add.onClick();
                          closeMenu();
                        }
                      }
                    }
                    choices={choices}
                    displayCustomValue={displayCustomValue}
                    emptyOption={emptyOption}
                    getItemProps={getItemProps}
                    hasMore={hasMore}
                    highlightedIndex={highlightedIndex}
                    loading={loading}
                    inputValue={inputValue}
                    isCustomValueSelected={isCustomValueSelected}
                    selectedItem={selectedItem}
                    onFetchMore={onFetchMore}
                    customValueLabel={customValueLabel}
                  />
                )}
              </div>
            );
          }}
        </Downshift>
      )}
    </DebounceAutocomplete>
  );
};

const SingleAutocompleteSelectField: React.FC<SingleAutocompleteSelectFieldProps> = ({
  choices,
  fetchChoices,
  retainChoices,
  ...rest
}) => {
  const [query, setQuery] = React.useState("");

  if (fetchChoices) {
    return (
      <DebounceAutocomplete
        debounceFn={query => {
          if (retainChoices) {
            if (rest.displayValue === query) {
              fetchChoices("");
            } else {
              fetchChoices(query);
            }
          } else {
            fetchChoices(query);
          }
        }}
      >
        {debounceFn => (
          <SingleAutocompleteSelectFieldComponent
            choices={choices}
            {...rest}
            fetchChoices={debounceFn}
          />
        )}
      </DebounceAutocomplete>
    );
  }

  return (
    <SingleAutocompleteSelectFieldComponent
      fetchChoices={q => setQuery(q || "")}
      choices={filter(choices, query, {
        key: "label"
      })}
      {...rest}
    />
  );
};
export default SingleAutocompleteSelectField;
